import { useMemo } from "react";
import type { Deal } from "@/lib/types/deals";
import { calculateEffectiveValue, hasValuation } from "@/lib/types/deals";
import { getDealType } from "@/lib/deals/getDealType";

interface SavingsIndicatorBadgeProps {
  deal: Deal;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: value >= 10 ? 0 : 2,
  }).format(value);
}

function deriveSavingsDisplay(deal: Deal): { label: string; value: string } | null {
  const { category } = getDealType(deal);

  if (deal.discount_percent != null && deal.discount_percent > 0) {
    return { label: "SAVE", value: `${Math.round(deal.discount_percent)}%` };
  }

  if (deal.effective_percent_back != null && deal.effective_percent_back > 0) {
    const percent = deal.effective_percent_back < 1
      ? Math.round(deal.effective_percent_back * 100)
      : Math.round(deal.effective_percent_back);
    return { label: "SAVE", value: `${percent}%` };
  }

  const effectiveValue = calculateEffectiveValue(deal);
  if (hasValuation(deal) && effectiveValue > 0) {
    if (category === "POINTS") {
      return { label: "VALUE", value: `${Math.round(effectiveValue)}%` };
    }
    return { label: "SAVE", value: `${Math.round(effectiveValue)}%` };
  }

  if (deal.computed_value_cad != null && deal.computed_value_cad > 0) {
    return { label: "VALUE", value: formatCurrency(deal.computed_value_cad) };
  }

  return null;
}

export function SavingsIndicatorBadge({ deal }: SavingsIndicatorBadgeProps) {
  const display = useMemo(() => deriveSavingsDisplay(deal), [deal]);
  if (!display) return null;

  return (
    <div
      data-testid="savings-indicator-badge"
      className="rounded-full border border-emerald-200/70 bg-[hsl(102_34%_91%)] text-emerald-900 shadow-sm"
      style={{
        minWidth: "4.2rem",
        minHeight: "4.2rem",
        boxShadow: "var(--wi-shadow-sm, 0 1px 2px rgba(0,0,0,0.08))",
      }}
    >
      <div className="flex h-full w-full flex-col items-center justify-center px-2 text-center leading-none">
        <span className="text-[10px] font-semibold tracking-[0.09em]">{display.label}</span>
        <span className="mt-0.5 text-[1.5rem] font-bold">{display.value}</span>
      </div>
    </div>
  );
}
